import { Period } from '@vy/types';
import { isNullOrUndefined, vyDate } from '@vy/utils';

export type TimelineTick = {
  label: string;
  months: number;
};

export type TimelineAxis = {
  ticks: TimelineTick[];
  totalMonths: number;
  // Size of the dominant axis unit in months (1 = month, 3 = quarter, 12 = year),
  // used to keep ticks above a readable minimum width.
  monthsPerTick: number;
  // The period the axis (and bar positions) are laid out against. For coarse units it is
  // padded to whole quarters/years so every tick is equal width; bars stay time-accurate
  // because they're positioned relative to this same domain.
  domain: Period;
};

// Above these durations the axis switches to a coarser unit so labels stay readable
// instead of collapsing one month-column per month into a few illegible pixels.
const QUARTER_AXIS_FROM_MONTHS = 24;
const YEAR_AXIS_FROM_MONTHS = 60;

export function buildTimelineAxis(period: Period): TimelineAxis {
  const totalMonths = getTotalMonths(period);

  if (totalMonths <= QUARTER_AXIS_FROM_MONTHS) {
    const months = Array.from({ length: totalMonths }, (_, index) =>
      vyDate(period.startTime).add(index, 'month'),
    );

    return {
      ticks: months.map((month) => ({ label: month.format('MMM YY'), months: 1 })),
      totalMonths,
      monthsPerTick: 1,
      domain: period,
    };
  }

  const useYears = totalMonths > YEAR_AXIS_FROM_MONTHS;
  const domain = useYears ? padToWholeYears(period) : padToWholeQuarters(period);
  const months = buildMonthsBetween(domain);

  return {
    ticks: groupMonthsIntoTicks(months, useYears),
    totalMonths: months.length,
    monthsPerTick: useYears ? 12 : 3,
    domain,
  };
}

function getTotalMonths(period: Period): number {
  const totalDays = vyDate(period.endTime).diff(vyDate(period.startTime), 'days', true);

  return Math.max(1, Math.ceil(totalDays / 30));
}

function padToWholeYears(period: Period): Period {
  return {
    startTime: vyDate(period.startTime).startOf('year').toDate(),
    endTime: vyDate(period.endTime).endOf('year').toDate(),
  };
}

function padToWholeQuarters(period: Period): Period {
  const start = vyDate(period.startTime);
  const end = vyDate(period.endTime);
  const firstQuarterMonth = Math.floor(start.month() / 3) * 3;
  const lastQuarterMonth = Math.floor(end.month() / 3) * 3 + 2;

  return {
    startTime: start.month(firstQuarterMonth).startOf('month').toDate(),
    endTime: end.month(lastQuarterMonth).endOf('month').toDate(),
  };
}

function buildMonthsBetween(period: Period): ReturnType<typeof vyDate>[] {
  const months: ReturnType<typeof vyDate>[] = [];
  const end = vyDate(period.endTime);
  let cursor = vyDate(period.startTime).startOf('month');

  while (cursor.isBefore(end)) {
    months.push(cursor);
    cursor = cursor.add(1, 'month');
  }

  return months;
}

function groupMonthsIntoTicks(months: ReturnType<typeof vyDate>[], useYears: boolean): TimelineTick[] {
  const ticks: TimelineTick[] = [];
  const tickIndexByKey = new Map<string, number>();

  for (const month of months) {
    const key = getGroupKey(month, useYears);
    const existingIndex = tickIndexByKey.get(key);

    if (isNullOrUndefined(existingIndex)) {
      tickIndexByKey.set(key, ticks.length);
      ticks.push({ label: getGroupLabel(month, useYears), months: 1 });
    } else {
      ticks[existingIndex].months += 1;
    }
  }

  return ticks;
}

function getGroupKey(month: ReturnType<typeof vyDate>, useYears: boolean): string {
  return useYears ? `${month.year()}` : `${month.year()}-${getQuarter(month)}`;
}

function getGroupLabel(month: ReturnType<typeof vyDate>, useYears: boolean): string {
  return useYears ? month.format('YYYY') : `Q${getQuarter(month)} ${month.format('YY')}`;
}

function getQuarter(month: ReturnType<typeof vyDate>): number {
  return Math.floor(month.month() / 3) + 1;
}
